/* Manager Handover Digest — Phase F.4
 *
 * Executive operational summary for managers returning to the home.
 * One report. 30-second scan. Answers: "What do I need to know right now?"
 *
 * Manager+ only.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  Loader2, FileDown, RefreshCw, Activity, AlertTriangle, AlertCircle,
  CheckCircle2, Sparkles, ShieldCheck, Heart, TrendingDown, TrendingUp,
  Users as UsersIcon, FileWarning, ListTodo, Star, AlertOctagon, Eye,
  Clock, Printer, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DigestSchedulesPanel from "@/components/handover/DigestSchedulesPanel";

const PERIODS = [
  { v: "shift", label: "Morning handover", sub: "Since last shift" },
  { v: "week",  label: "Weekly management", sub: "Last 7 days" },
  { v: "month", label: "Monthly oversight", sub: "Last 30 days" },
];

const TONE = {
  red:   { fg: "#7a1a28", bg: "#FBE3E7", line: "#A8273A" },
  amber: { fg: "#7a4d12", bg: "#FCEFD4", line: "#B8772F" },
  green: { fg: "#1f4f2b", bg: "#E7F3EC", line: "#2F6A3A" },
  grey:  { fg: "#5d6068", bg: "#F1EFEC", line: "#5d6068" },
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function HandoverDigest() {
  const [tab, setTab] = useState("digest");
  const [period, setPeriod] = useState("shift");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (tab !== "digest") return;
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/handover/digest?period=${period}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.status === 403
        ? "Manager+ only — Handover Digest is restricted."
        : "Could not load digest.");
    } finally { setLoading(false); }
  }, [period, tab]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/handover/digest.pdf?period=${period}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `manager-handover-digest-${period}-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Handover digest downloaded");
    } catch { toast.error("Could not download PDF"); }
    finally { setDownloading(false); }
  };

  const emailDraft = () => {
    if (!data) return;
    const subject = encodeURIComponent(`Handover digest · ${data.period_label}`);
    const body = encodeURIComponent(
      `Manager Handover Digest\n\n` +
      `Period: ${data.period_label}\n` +
      `Generated by: ${data.generated_by}\n` +
      `Generated at: ${fmt(data.generated_at)}\n\n` +
      `Manager actions outstanding: ${data.manager_actions.total}\n` +
      `Safeguarding · new ${data.safeguarding.new_count} · open ${data.safeguarding.open_count} · escalated ${data.safeguarding.escalated_count}\n` +
      `Missing · ${data.missing.episodes_count} episodes · ${data.missing.outstanding_interviews} interview(s) outstanding\n` +
      `Incidents · physical ${data.incidents.physical_count} · high-risk ${data.incidents.high_risk_count} · police ${data.incidents.police_count}\n` +
      `Placement stability · ${data.placement_stability.improving_count} improving · ${data.placement_stability.deteriorating_count} support recommended\n` +
      `Compliance · ${data.compliance.overdue_supervisions} supervisions overdue · ${data.compliance.expiring_dbs} DBS expiring\n\n` +
      `Open the live digest in Safelyn for the full evidence and recommended actions.`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const TabBar = (
    <div className="flex items-center gap-1 p-1 bg-stone-100 rounded-xl w-fit print:hidden">
      <button
        type="button"
        onClick={() => setTab("digest")}
        data-testid="handover-tab-digest"
        className={`px-4 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
          tab === "digest" ? "bg-white text-[#0F2A47] shadow-sm" : "text-stone-600 hover:bg-white/50"
        }`}
      >
        Digest
      </button>
      <button
        type="button"
        onClick={() => setTab("schedules")}
        data-testid="handover-tab-schedules"
        className={`px-4 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
          tab === "schedules" ? "bg-white text-[#0F2A47] shadow-sm" : "text-stone-600 hover:bg-white/50"
        }`}
      >
        Schedules
      </button>
    </div>
  );

  if (tab === "schedules") {
    return (
      <div className="space-y-4 max-w-7xl mx-auto" data-testid="handover-digest-page">
        {TabBar}
        <DigestSchedulesPanel />
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="space-y-4 max-w-7xl mx-auto" data-testid="handover-digest-page">
        {TabBar}
        <div className="bg-white border divider-soft rounded-2xl p-6 flex items-center gap-2 text-stone-600 text-sm">
          <Loader2 size={14} className="animate-spin" /> Compiling handover digest…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-4 max-w-7xl mx-auto" data-testid="handover-digest-page">
        {TabBar}
        <div className="bg-white border divider-soft rounded-2xl p-6 text-sm text-stone-700">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4 max-w-7xl mx-auto print:max-w-none" data-testid="handover-digest-page">
      {TabBar}
      {/* Hero */}
      <header
        className="rounded-2xl p-5 sm:p-6 relative overflow-hidden print:bg-white print:text-black print:p-3"
        style={{ background: "linear-gradient(135deg, #0E3B4A 0%, #0a2734 60%, #1E4D5C 100%)", color: "white" }}
      >
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#B8772F]/10 blur-3xl print:hidden" aria-hidden />
        <div className="relative">
          <div className="flex items-center gap-2 print:text-[#0F2A47]">
            <Activity size={14} className="text-[#FCB960] print:text-[#B8772F]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/70 print:text-[#0F2A47]">
              Manager Handover Digest · Operational heartbeat
            </span>
          </div>
          <h1 className="font-display font-semibold text-2xl sm:text-3xl mt-1.5 print:text-black"
            style={{ letterSpacing: "-0.02em" }}>
            {data.period_label}
          </h1>
          <p className="text-[12px] text-white/65 mt-1 max-w-2xl print:text-stone-700">
            One scan. Everything important. {fmt(data.period_start)} → {fmt(data.period_end)} ·
            Generated by <strong>{data.generated_by}</strong>
          </p>

          {/* Period selector + actions */}
          <div className="flex items-center gap-2 mt-4 flex-wrap print:hidden">
            <div className="bg-white/10 rounded-lg p-1 flex gap-1 border border-white/10">
              {PERIODS.map((p) => (
                <button key={p.v} type="button" onClick={() => setPeriod(p.v)}
                  className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
                    period === p.v ? "bg-white text-[#0F2A47]" : "text-white/85 hover:bg-white/10"
                  }`}
                  data-testid={`handover-period-${p.v}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <Button onClick={downloadPdf} disabled={downloading}
              className="bg-[#B8772F] hover:bg-[#a3661f] text-white text-[12px] h-8 ml-2"
              data-testid="handover-download-pdf">
              {downloading ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <FileDown size={12} className="mr-1.5" />}
              PDF
            </Button>
            <Button onClick={() => window.print()} variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-[12px] h-8"
              data-testid="handover-print-btn">
              <Printer size={12} className="mr-1.5" /> Print
            </Button>
            <Button onClick={emailDraft} variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-[12px] h-8"
              data-testid="handover-email-btn">
              <Mail size={12} className="mr-1.5" /> Email
            </Button>
            <button type="button" onClick={load}
              className="text-white/70 hover:text-white p-1.5 rounded hover:bg-white/10"
              title="Refresh">
              <RefreshCw size={12} />
            </button>
          </div>
        </div>
      </header>

      {/* Manager Actions Required — MOST IMPORTANT */}
      <section className="bg-white border-2 rounded-2xl p-4 sm:p-5"
        style={{ borderColor: data.manager_actions.total > 0 ? "#A8273A" : "#2F6A3A" }}
        data-testid="handover-actions-required">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <AlertOctagon size={14} className={data.manager_actions.total > 0 ? "text-[#A8273A]" : "text-[#2F6A3A]"} />
          <h2 className="font-display font-semibold text-lg text-[#0F1115]">
            What do I need to do today?
          </h2>
          <span className="text-[11px] uppercase tracking-wider text-stone-500 ml-1">
            {data.manager_actions.total} action{data.manager_actions.total === 1 ? "" : "s"} outstanding
          </span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <ActionPanel title="Urgent" items={data.manager_actions.urgent} tone="red" testid="actions-urgent" />
          <ActionPanel title="Overdue" items={data.manager_actions.overdue} tone="amber" testid="actions-overdue" />
          <ActionPanel title="Due today" items={data.manager_actions.due_today} tone="grey" testid="actions-due-today" />
        </div>
        {data.manager_actions.safeguarding_actions.length > 0 && (
          <div className="mt-3 rounded-lg bg-[#FBE3E7] p-3 border-l-4 border-[#A8273A]"
            data-testid="actions-safeguarding">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#7a1a28] mb-1">
              Safeguarding actions
            </div>
            <ul className="space-y-1">
              {data.manager_actions.safeguarding_actions.map((a, i) => (
                <li key={i} className="text-[12px] text-[#0F1115]">• {a.title}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* KPI tiles */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="handover-kpi-tiles">
        <KPI Icon={ShieldCheck} label="Safeguarding new"   value={data.safeguarding.new_count}        tone={data.safeguarding.new_count ? "red" : "grey"} />
        <KPI Icon={ShieldCheck} label="Safeguarding open"  value={data.safeguarding.open_count}       tone={data.safeguarding.open_count ? "amber" : "grey"} />
        <KPI Icon={Activity}    label="Missing episodes"   value={data.missing.episodes_count}        tone={data.missing.episodes_count ? "amber" : "grey"} />
        <KPI Icon={AlertTriangle} label="High-risk incidents" value={data.incidents.high_risk_count} tone={data.incidents.high_risk_count ? "red" : "grey"} />
        <KPI Icon={TrendingDown} label="Improving"          value={data.placement_stability.improving_count} tone={data.placement_stability.improving_count ? "green" : "grey"} />
        <KPI Icon={TrendingUp}   label="Support recommended" value={data.placement_stability.deteriorating_count} tone={data.placement_stability.deteriorating_count ? "red" : "grey"} />
        <KPI Icon={FileWarning}  label="Supervisions overdue" value={data.compliance.overdue_supervisions} tone={data.compliance.overdue_supervisions ? "red" : "grey"} />
        <KPI Icon={UsersIcon}    label="Burnout alerts"      value={data.staffing.burnout_alert_count} tone={data.staffing.burnout_alert_count ? "amber" : "grey"} />
      </section>

      {/* Child Spotlight */}
      <section className="grid sm:grid-cols-3 gap-3" data-testid="handover-spotlight">
        <SpotlightCard title="Most improved"
          icon={Star} accent="#2F6A3A" item={data.child_spotlight.most_improved}
          fallback="No standout improvement this period." testid="spotlight-improved" />
        <SpotlightCard title="Highest concern"
          icon={AlertTriangle} accent="#A8273A" item={data.child_spotlight.highest_concern}
          fallback="No major concerns flagged." testid="spotlight-concern" />
        <SpotlightCard title="Review required"
          icon={Eye} accent="#B8772F" item={data.child_spotlight.review_required}
          fallback="No reviews flagged." testid="spotlight-review" />
      </section>

      {/* Three-column body */}
      <div className="grid lg:grid-cols-3 gap-3">
        {/* Safeguarding & Missing */}
        <Section title="Safeguarding & Missing" Icon={ShieldCheck} testid="section-safeguarding">
          <StatLine label="New safeguarding" value={data.safeguarding.new_count} tone={data.safeguarding.new_count ? "red" : "green"} />
          <StatLine label="Open safeguarding" value={data.safeguarding.open_count} tone={data.safeguarding.open_count ? "amber" : "green"} />
          <StatLine label="Closed safeguarding" value={data.safeguarding.closed_count} tone="green" />
          <StatLine label="Escalated" value={data.safeguarding.escalated_count} tone={data.safeguarding.escalated_count ? "red" : "green"} />
          <StatLine label="Reg 40 notifications" value={data.safeguarding.reg40_count} tone={data.safeguarding.reg40_count ? "amber" : "green"} />
          <div className="border-t divider-soft my-2" />
          <StatLine label="Missing episodes" value={data.missing.episodes_count} tone={data.missing.episodes_count ? "amber" : "green"} />
          <StatLine label="Return interviews outstanding" value={data.missing.outstanding_interviews} tone={data.missing.outstanding_interviews ? "red" : "green"} />
          <StatLine label="Repeat-pattern children" value={data.missing.repeat_count} tone={data.missing.repeat_count ? "red" : "green"} />
          {data.missing.top_affected.length > 0 && (
            <div className="mt-2 text-[10px] text-stone-500">
              Most affected: {data.missing.top_affected.map((c) => `${c.resident_name} (${c.count})`).join(" · ")}
            </div>
          )}
        </Section>

        {/* Incidents & Placement */}
        <Section title="Incidents & Placement" Icon={AlertCircle} testid="section-incidents">
          <StatLine label="Physical interventions" value={data.incidents.physical_count} tone={data.incidents.physical_count ? "amber" : "green"} />
          <StatLine label="High-risk incidents" value={data.incidents.high_risk_count} tone={data.incidents.high_risk_count ? "red" : "green"} />
          <StatLine label="Police-involved" value={data.incidents.police_count} tone={data.incidents.police_count ? "red" : "green"} />
          <StatLine label="Damage incidents" value={data.incidents.damage_count} tone={data.incidents.damage_count ? "amber" : "green"} />
          {data.incidents.patterns.length > 0 && (
            <div className="mt-1.5 bg-[#FCEFD4] rounded p-2">
              <div className="text-[9px] font-bold uppercase tracking-wider text-[#7a4d12] mb-0.5">Patterns emerging</div>
              {data.incidents.patterns.map((p, i) => (
                <div key={i} className="text-[10px] text-[#7a4d12]">
                  {p.category}: {p.previous_period} → {p.this_period} ({p.direction})
                </div>
              ))}
            </div>
          )}
          <div className="border-t divider-soft my-2" />
          <StatLine label="Children improving" value={data.placement_stability.improving_count} tone="green" />
          <StatLine label="Support recommended" value={data.placement_stability.deteriorating_count} tone={data.placement_stability.deteriorating_count ? "red" : "green"} />
          {data.placement_stability.improving.length > 0 && (
            <div className="mt-1.5 text-[10px] text-[#2F6A3A]">
              Stabilising: {data.placement_stability.improving.slice(0,3).map((c) => c.resident_name).join(" · ")}
            </div>
          )}
          {data.placement_stability.deteriorating.length > 0 && (
            <div className="mt-1 text-[10px] text-[#A8273A]">
              Needs support: {data.placement_stability.deteriorating.slice(0,3).map((c) => c.resident_name).join(" · ")}
            </div>
          )}
        </Section>

        {/* Staffing & Compliance */}
        <Section title="Staffing & Compliance" Icon={UsersIcon} testid="section-staffing">
          <StatLine label="Shifts this period" value={data.staffing.shifts_count} tone="grey" />
          <StatLine label="Sickness records" value={data.staffing.sickness_count} tone={data.staffing.sickness_count ? "amber" : "green"} />
          <StatLine label="Agency staff active" value={data.staffing.agency_count} tone="grey" />
          <StatLine label="Burnout alerts" value={data.staffing.burnout_alert_count} tone={data.staffing.burnout_alert_count ? "amber" : "green"} />
          {data.staffing.burnout_alerts.length > 0 && (
            <div className="mt-1 text-[10px] text-stone-500">
              {data.staffing.burnout_alerts.slice(0,3).map((b) => `${b.name} (${b.risk_level})`).join(" · ")}
            </div>
          )}
          <div className="border-t divider-soft my-2" />
          <StatLine label="Supervisions overdue" value={data.compliance.overdue_supervisions} tone={data.compliance.overdue_supervisions ? "red" : "green"} />
          <StatLine label="Expiring DBS (60d)" value={data.compliance.expiring_dbs} tone={data.compliance.expiring_dbs ? "amber" : "green"} />
          <StatLine label="Expired training" value={data.compliance.expired_training} tone={data.compliance.expired_training ? "red" : "green"} />
          <StatLine label="SCR · red staff" value={data.compliance.scr_red_count} tone={data.compliance.scr_red_count ? "red" : "green"} />
          <StatLine label="Open inspection actions" value={data.compliance.open_actions} tone={data.compliance.open_actions ? "amber" : "green"} />
        </Section>
      </div>

      {/* Home intelligence */}
      <section className="bg-white border divider-soft rounded-2xl p-4 sm:p-5"
        data-testid="handover-home-intelligence">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-[#0e3b4a]" />
          <h2 className="font-display font-semibold text-lg text-[#0F1115]">
            Home intelligence
          </h2>
          <span className="text-[10px] uppercase tracking-wider text-stone-500 ml-1">
            Cross-module pattern alerts
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <IntelBlock title="Alerts" items={data.home_intelligence.alerts}
            tone="#A8273A" emptyText="No alerts this period." testid="intel-alerts" />
          <div className="space-y-3">
            {data.home_intelligence.recommendations.length > 0 && (
              <IntelBlock title="Recommendations"
                items={data.home_intelligence.recommendations}
                tone="#0e3b4a" prefix="→" testid="intel-recs" />
            )}
            {data.home_intelligence.positives.length > 0 && (
              <IntelBlock title="Positives" items={data.home_intelligence.positives}
                tone="#2F6A3A" prefix="✓" testid="intel-positives" />
            )}
          </div>
        </div>
      </section>

      <p className="text-[11px] text-stone-500 px-1 leading-relaxed">
        {data.explainable_note}
      </p>

      <style>{`@media print {
        body { background: white !important; }
        .print\\:hidden { display: none !important; }
        section { break-inside: avoid; }
      }`}</style>
    </div>
  );
}

function ActionPanel({ title, items, tone, testid }) {
  const t = TONE[tone] || TONE.grey;
  return (
    <div className="bg-stone-50 rounded-lg p-3" style={{ borderLeft: `3px solid ${t.line}` }}
      data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: t.fg }}>
        {title} · {items.length}
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] text-stone-500 italic">None.</p>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 5).map((a, i) => (
            <li key={i} className="text-[12px] text-[#0F1115]">
              • {a.title} <span className="text-[10px] text-stone-500">· {a.category}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KPI({ Icon, label, value, tone }) {
  const t = TONE[tone] || TONE.grey;
  return (
    <div className="bg-white border divider-soft rounded-xl p-3" style={{ borderLeft: `3px solid ${t.line}` }}>
      <div className="flex items-center gap-1.5">
        <Icon size={11} style={{ color: t.line }} />
        <span className="text-[9px] font-bold uppercase tracking-wider text-stone-500">{label}</span>
      </div>
      <div className="font-display font-semibold text-2xl mt-0.5" style={{ color: t.line }}>
        {value ?? 0}
      </div>
    </div>
  );
}

function SpotlightCard({ title, icon: Icon, accent, item, fallback, testid }) {
  return (
    <div className="bg-white border divider-soft rounded-xl p-3.5"
      style={{ borderLeft: `4px solid ${accent}` }} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={12} style={{ color: accent }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>{title}</span>
      </div>
      {item ? (
        <div>
          <div className="font-semibold text-[14px] text-[#0F1115]">{item.resident_name}</div>
          <p className="text-[11px] text-stone-600 mt-1 leading-relaxed">{item.why}</p>
          {item.evidence && (
            <p className="text-[10px] text-stone-500 mt-1 italic">{item.evidence}</p>
          )}
          <div className="text-[10px] text-stone-700 mt-2 pt-2 border-t divider-soft">
            <strong className="uppercase tracking-wider text-[9px]" style={{ color: accent }}>Action: </strong>
            {item.recommended_action}
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-stone-500 italic">{fallback}</p>
      )}
    </div>
  );
}

function Section({ title, Icon, children, testid }) {
  return (
    <section className="bg-white border divider-soft rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} className="text-[#0e3b4a]" />
        <h3 className="font-display font-semibold text-sm text-[#0F1115]">{title}</h3>
      </div>
      <div className="space-y-0.5">{children}</div>
    </section>
  );
}

function StatLine({ label, value, tone }) {
  const t = TONE[tone] || TONE.grey;
  return (
    <div className="flex items-center justify-between py-1 text-[12px]">
      <span className="text-stone-700">{label}</span>
      <span className="font-display font-semibold text-base" style={{ color: t.line }}>{value}</span>
    </div>
  );
}

function IntelBlock({ title, items, tone, prefix = "•", emptyText, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: tone }}>
        {title}
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] text-stone-500 italic">{emptyText || "None."}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((s, i) => (
            <li key={i} className="text-[12px] text-[#0F1115] leading-relaxed">
              <span style={{ color: tone }}>{prefix}</span> {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
